import './processing.scss';

import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import { Col, Row } from 'antd';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { IApplicationState } from '../../store';

import Heading, { HeadingSize } from '../../components/heading/heading';

import { actionCreators as PaymentActions, PaymentMethodTypes } from '../../store/Payments';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import { actionCreators as BookingActions } from '../../store/Booking';
import { actionCreators as GiftVoucherActions } from '../../store/GiftVoucher';
import { actionCreators as BalancePaymentLandingActions } from '../../store/BalancePaymentLanding';
import adBlockChecker from '../../helpers/adBlockChecker';
import { PurchaseTypes } from '../../store/PurchaseTypes';
import { Routes, navigateTo } from '../../helpers/routes';
 import { appInsights } from "../../Logging/appInsights";

const ProcessingPage: React.FunctionComponent = () => {
    const { t } = useTranslation();

    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const backProcessing = useSelector((state: IApplicationState) => state.payments.backProcessing);
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const clientSecret = useSelector((state: IApplicationState) => state.payments.clientSecret);

    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);

    let adBlockDetected = false;
    adBlockChecker.IsAdBlockEnabled().then((flag) => {
        adBlockDetected = flag;
    });

    useEffect(() => {
        // Rebuild params from location.search so the effect has an honest, stable
        // dependency (useQueryString returns a fresh URLSearchParams every render).
        const params = new URLSearchParams(location.search);
        const redirectStatus = params.get('redirect_status');
        const paymentIntent = params.get('payment_intent');
        const paymentIntentClientSecret = params.get('payment_intent_client_secret');

        if (paymentIntent && paymentIntentClientSecret && redirectStatus) {
            console.log('redirectStatus: ' + redirectStatus);
            if (redirectStatus === 'failed') {
                console.log('redirect status is FAILED, push user to checkout');
                dispatch(navigateTo(Routes.Checkout, selectedSite?.urlKey));
            } else if (redirectStatus === 'succeeded') {
                console.log('redirect status is SUCCEEDED, awaiting confirmation');

                // Balance payment runs regardless of REACT_APP_CONFIRM_PAYMENTS: there's no
                // synchronous confirm endpoint for the Stripe balance flow, so polling the
                // outstanding balance until the webhook lands is the only mechanism that
                // moves the user off this page.
                if (selectedPurchaseType === PurchaseTypes.BalancePayment) {
                    const bookingRef = params.get('bookingRef');
                    if (bookingRef) {
                        console.log('balance payment redirect succeeded, polling for paid status');
                        dispatch(BalancePaymentLandingActions.pollBalanceUntilPaid(bookingRef));
                    }
                } else if (Number(process.env.REACT_APP_CONFIRM_PAYMENTS) === 1) {
                    if (
                        selectedPurchaseType === PurchaseTypes.Booking ||
                        selectedPurchaseType === PurchaseTypes.PrivateEvent
                    ) {
                        console.log('attempt confirm booking or private event');
                        dispatch(
                            BookingActions.confirmBooking(
                                { StripeChargeId: paymentIntent },
                                paymentIntent,
                                Number(PaymentMethodTypes.Stripe),
                                adBlockDetected
                            )
                        );
                    } else if (selectedPurchaseType === PurchaseTypes.GiftVoucher || selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher) {
                        console.log('attempt confirm gift voucher');
                        dispatch(
                            GiftVoucherActions.confirmGiftVoucher(
                                { StripeChargeId: paymentIntent },
                                paymentIntent,
                                Number(PaymentMethodTypes.Stripe),
                                adBlockDetected
                            )
                        );
                    } else if (selectedPurchaseType === PurchaseTypes.SplitPayment) {
                        // Split payment confirmation is handled by polling the share status
                        // in the reservation timer component — no confirmBooking call needed
                        console.log('split payment redirect succeeded, awaiting share status poll');
                    } else {
                        let errMsg = 'No Purchase type was selected when trying to confirm purchase';
                        console.error(errMsg);
                         appInsights?.trackException(
                             { error: new Error(`Error processing payment : ${errMsg}`), severityLevel: 2},
                             { sourceFile: "processing.tsx", product: "booking"  });
                    }
                }
            }
        }
    }, [dispatch, clientSecret, location.search, selectedPurchaseType, selectedSite?.urlKey, adBlockDetected]);

    useEffect(() => {
        if (!backProcessing) {
            dispatch(PaymentActions.backProcessingAdded());

            // Clone location object and push it to history
            history.push({
                pathname: location.pathname,
                search: location.search,
            });
        }
    }, [backProcessing, dispatch, history, location.pathname, location.search]);

    useEffect(() => {
        return history.listen((newLocation, action) => {
            // listen
            if (action === 'POP') {
                console.log('action is POP');
                history.go(1);
            }
        });
    }, [history]);

    return (
        <React.Fragment>
            <Row justify="center" align="middle" className="processing-container">
                <Col className="text-center">
                    <Heading level={HeadingSize.One} className="massive">
                        <LoadingSpinner />
                    </Heading>
                    <Heading level={HeadingSize.Two} className="massive">
                        {t('pageTitles.processingBooking')}
                    </Heading>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default ProcessingPage;
